import React from 'react';
import { getService } from '@Client/runner.hooks/useService';
import EmptyState from '@Client/runner.directives/common/EmptyState';
import FlowIconSquare from '@Client/runner.directives/common/FlowIconSquare/FlowIconSquare';
import IPublishRequestOverview from '@Shared.Angular/@types/core/contracts/queryModel/governance/publishRequestOverview';
import PublishRequestEntityType from '@Shared.Angular/@types/core/contracts/constants/publishRequestEntityType';
import { ProcedureViewMode } from '@Shared.Angular/@types/core/contracts/constants/procedureViewMode';
import { STATE_MAPPINGS } from '@Shared.Angular/@types/stateMappings';

interface Props {
  isMobile: boolean;
  filteredItems: IPublishRequestOverview[];
  roleLabel: string;
  displayField: string;
  emptyStateData: {
    summaryText: string;
    bodyText: string;
  };
}

const ApprovalList: React.FC<Props> = ({
  isMobile,
  filteredItems,
  roleLabel,
  displayField,
  emptyStateData
}) => {
  const flowinglyMomentService = getService('flowinglyMomentService');
  const tempModelerUrlService = getService('tempModelerUrlService');
  const $state = getService('$state');

  const getOverdueStatus = (dueBy: string | null) => {
    const isOverdue = dueBy
      ? new Date(dueBy).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0)
      : false;

    return {
      statusText: isOverdue ? 'Overdue' : 'Due By',
      statusClass: isOverdue ? 'color-warning' : ''
    };
  };

  return filteredItems.length > 0 ? (
    <div className={`flow-block ${isMobile ? 'isMobile' : ''}`}>
      <div className={`flow-list ${isMobile ? 'isMobile' : ''}`}>
        <ul>
          {filteredItems.map((publishRequest, index) => {
            const { statusText, statusClass } = getOverdueStatus(
              publishRequest.approvalDueUtc
            );

            return (
              <li key={`${publishRequest.entityId}-${index}`}>
                <div
                  className="flow-start"
                  id={`flowactive_${publishRequest.entityId}`}
                >
                  <div
                    className="flow-start-header flex flex-row"
                    data-testid="governance-publish-request"
                    onClick={() =>
                      publishRequest.entityType ===
                      PublishRequestEntityType.FlowModel
                        ? tempModelerUrlService.openModeler({
                            flowModelId: publishRequest.entityId,
                            loadPublishRequestSchema: true
                          })
                        : $state.go(
                            STATE_MAPPINGS.procedure,
                            {
                              procedureId: publishRequest.entityId,
                              viewMode: ProcedureViewMode.PublishRequest
                            },
                            {}
                          )
                    }
                  >
                    {!isMobile && (
                      <FlowIconSquare
                        name={publishRequest.entityName}
                        flowCode=""
                      />
                    )}
                    {!isMobile && (
                      <div className="governance-list-contents flex flex-row">
                        <div className="governance-list-item">
                          <div className="governance-list-item-text flex-column">
                            {publishRequest.entityName}
                          </div>
                          <div className="governance-list-item-sub-text flex-column">
                            {publishRequest.categoryName}
                          </div>
                        </div>
                        <div className="governance-list-item">
                          <div className="governance-list-item-text flex-column">
                            {publishRequest.entityDescription || '\u00A0'}
                          </div>
                          <div className="governance-list-item-sub-text flex-column">
                            {publishRequest.entityTypeDescription}
                          </div>
                        </div>
                        <div className="governance-list-item">
                          <div className="governance-list-item-text flex-column">
                            <div>{roleLabel}</div>
                            <div className="governance-list-item-sub-text flex-column">
                              {publishRequest[displayField]}
                            </div>
                          </div>
                        </div>
                        <div className="governance-list-item">
                          <div
                            className={`governance-list-item-text flex-column ${statusClass}`}
                          >
                            <div>{statusText}</div>
                            <div className="governance-list-item-sub-text flex-column">
                              {publishRequest.approvalDueUtc &&
                                flowinglyMomentService.formatUTCToLocal(
                                  publishRequest.approvalDueUtc
                                )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {isMobile && (
                      <div className="flow-start-mobile">
                        <div className="flex flow-header__main">
                          <FlowIconSquare
                            name={publishRequest.entityName}
                            flowCode=""
                          />
                          <div className="flow-list__contents flex flex-row">
                            <div className="flow-list__contents__subject--mobile mr-20">
                              <div className="flow-list__contents__subject__text flex-column">
                                {publishRequest.entityName}
                              </div>
                              <div className="flow-list__contents__subject__started-by flex-column">
                                {publishRequest.categoryName}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flow-header__extra flow-header__extra--start-flow">
                          {publishRequest.entityDescription}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  ) : (
    <div className={`governance-list-empty ${isMobile ? 'isMobile' : ''}`}>
      <EmptyState
        summaryText={emptyStateData.summaryText}
        bodyText={emptyStateData.bodyText}
      />
    </div>
  );
};

export default ApprovalList;
